/*
 * The language the site's own words are shown in, for THIS browser (S48,
 * 2026-10-29).
 *
 * Two languages, US English ("en-us", the default) and Danish ("da-dk"), chosen
 * in the header's dropdown (LanguageSwitch.tsx). What changes is the site's OWN
 * text - the navigation, the filter panels, the counts, the dates, the progress
 * and error lines, the sign-in cards - which lives in the two dictionaries beside
 * this file, en.ts and da.ts. What does NOT change is anything an author or an
 * administrator wrote: article and release-note text, titles, product and
 * category names, and the headings set on the Settings page. The content is
 * single-language by decision (S18 removed its translations on 2026-08-25), and
 * this does not bring them back.
 *
 * WHY THIS IS NOT A SITE SETTING. As with light and dark (ColorScheme.ts), the
 * language is the reader's, not the site's: remembered in localStorage, per
 * browser, never sent anywhere. Nothing stored means the default, English - the
 * browser's own language is not consulted, by the owner's "en-us (default)".
 *
 * ONE STORE, NOT ONE STATE PER COMPONENT. Dozens of components read the
 * language, so it is held once here and read through useSyncExternalStore: a
 * choice made in the switch re-renders every reader of it, and two switches
 * (there is one per header) cannot disagree, which is the trap the colour-scheme
 * switch documents for itself.
 *
 * DATES keep the order the site has always shown in English, "28 October 2026",
 * rather than the American "October 28, 2026": the English dictionary is the
 * text the site already had, and that is how its dates read. Until S48 they
 * followed the browser's locale; now they follow the chosen language, so the
 * month name always matches the words around it.
 */

import { useSyncExternalStore } from 'react';
import { da } from './da.js';
import { en, type Messages } from './en.js';

export type LanguageCode = 'en-us' | 'da-dk';

export interface Language {
  code: LanguageCode;
  /** What the switch shows beside the flag. */
  short: string;
  /** The language's name in its own words: each option's accessible name. */
  name: string;
  /** For <html lang> and each option's own `lang`. */
  htmlLang: string;
  /** The locale dates and numbers are written in - see DATES above. */
  dateLocale: string;
  messages: Messages;
}

export const LANGUAGES: readonly Language[] = [
  { code: 'en-us', short: 'EN', name: 'English', htmlLang: 'en-US', dateLocale: 'en-GB', messages: en },
  { code: 'da-dk', short: 'DA', name: 'Dansk', htmlLang: 'da-DK', dateLocale: 'da-DK', messages: da },
];

export const DEFAULT_LANGUAGE: LanguageCode = 'en-us';

const STORAGE_KEY = 'kb.language';

function isLanguageCode(value: unknown): value is LanguageCode {
  return LANGUAGES.some((language) => language.code === value);
}

export function languageFor(code: LanguageCode): Language {
  return LANGUAGES.find((language) => language.code === code) ?? LANGUAGES[0]!;
}

/** The remembered choice, or the default when none was made or storage is unavailable. */
export function readRememberedLanguage(): LanguageCode {
  try {
    const remembered = localStorage.getItem(STORAGE_KEY);
    return isLanguageCode(remembered) ? remembered : DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

// Read from storage on first use, then held here.
let current: LanguageCode | null = null;
const listeners = new Set<() => void>();

function snapshot(): LanguageCode {
  if (current === null) current = readRememberedLanguage();
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function markDocument(code: LanguageCode): void {
  document.documentElement.lang = languageFor(code).htmlLang;
}

/**
 * The remembered language onto <html lang> before the first render. Called once
 * from main.tsx, beside applyRememberedColorScheme, so a screen reader and the
 * browser's own hyphenation meet the right language from the first paint.
 */
export function applyRememberedLanguage(): void {
  current = readRememberedLanguage();
  markDocument(current);
}

/** Choose a language for this browser: shown at once, and remembered. */
export function chooseLanguage(code: LanguageCode): void {
  current = code;
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // No storage: the choice holds for this page and is forgotten with it.
  }
  markDocument(code);
  for (const listener of listeners) listener();
}

export interface LanguageState {
  language: Language;
  choose: (code: LanguageCode) => void;
}

export function useLanguage(): LanguageState {
  const code = useSyncExternalStore(subscribe, snapshot, snapshot);
  return { language: languageFor(code), choose: chooseLanguage };
}

/** The current language's words. Most components need nothing else. */
export function useMessages(): Messages {
  return useLanguage().language.messages;
}

/**
 * Back to a browser that never chose: the choice in memory and in storage
 * forgotten, <html lang> left to index.html's. For tests, which share this
 * module within a file - test/setup.ts calls it after each one.
 */
export function resetLanguageForTests(): void {
  current = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored to forget.
  }
  document.documentElement.removeAttribute('lang');
  for (const listener of listeners) listener();
}
